import { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState
} from 'recoil'

import { evalCondition } from 'mailup-react-helpers'

import {
  Loading,
  ModalConfirm,
  TOAST_VARIANT_INFO,
  TOAST_VARIANT_WARNING,
  ToastContainer,
  Tooltip,
  toastShow
} from 'mailup-web-components'

import ErrorFeedback from 'Components/ErrorFeedback'
import PageContainer from 'Components/PageContainer'
import Header from 'Pages/EmailSendingWizard/components/Header/Header'

import SendingsLimitWarningMessage from 'Pages/EmailSendingWizard/components/SendingsLimitWarningMessage'
import {
  cleanupCallback,
  deleteRecipientsCallback,
  fetchDynamicFields,
  initSendingWizardCallback,
  putBeListSenderInfoCallback,
  sendingSegmentationSimulateCallback
} from 'Pages/EmailSendingWizard/recoil/callbacks/callback'
import { pageState } from 'Pages/EmailSendingWizard/recoil/state/page'
import WizardContainer from './components/WizardContainer'

import ModalConfirmLeave from 'Components/ModalConfirmLeave/ModalConfirmLeave'
import ModalSendingSegmentation from 'Components/ModalSendingSegmentation'
import { messageDetailsState } from 'Hooks/useMessageDetails/state'
import useMessageDetails from 'Hooks/useMessageDetails/useMessageDetails'
import useBeforeUnload from 'Hooks/useRouteBeforeUnload'
import { useIntl } from 'react-intl'
import ModalCheckup from '../../components/ModalCheckup'
import { modalCheckupState } from '../../components/ModalCheckup/recoil/state'
import { ModalDelete } from './components/BoxSendingManage/ModalDelete'
import ModalColumnsManagement from './components/ResultsPreview/ModalColumnsManagement'
import SegmentationMessageBox from './components/SegmentationMessageBox'
import { fasciaDiNonDisturboState } from './recoil/state/fasciaDiNonDisturbo'
import {
  modalDeleteState,
  modalSaveDefaultState,
  modalSchedulingState,
  modalSendingSegmentationState
} from './recoil/state/modals'
import { manageButtonsState } from './recoil/state/queue'

const EmailSendingWizard = () => {
  const history = useHistory()
  const { id } = useParams()
  const intl = useIntl()
  const { loading, loaded, error, severity } = useRecoilValue(pageState)
  const init = useRecoilCallback(initSendingWizardCallback, [])
  const initCustomFields = useRecoilCallback(fetchDynamicFields, [])
  const cleanup = useRecoilCallback(cleanupCallback, [])
  const { isFethingModalState, error: modalCheckupError } = useRecoilValue(
    modalCheckupState
  )
  const { show: showScheduleModal, startDate } = useRecoilValue(
    modalSchedulingState
  )
  const { isDirty } = useRecoilValue(fasciaDiNonDisturboState)
  useBeforeUnload(isDirty)
  useMessageDetails(parseInt(id, 0))

  const { isBee, messageType, campaign } = useRecoilValue(
    messageDetailsState(parseInt(id, 0))
  )

  const onQueueRetried = () => {
    history.push('/messages/email/sendstatus/campaigns')
  }

  const onTrialRecipientLimit = remainingSendings => {
    const goToMShop = evalCondition(
      'uo:GEST AND NOT c:IsRetailer AND NOT c:IsDeveloper AND NOT co:TURNOFFMSHOP'
    )
    toastShow(
      <SendingsLimitWarningMessage
        remainingSendings={remainingSendings}
        goToMShop={goToMShop}
      />,
      remainingSendings > 0 ? TOAST_VARIANT_INFO : TOAST_VARIANT_WARNING,
      'trialSendingsLimitWarning',
      remainingSendings > 0
    )
  }

  const initComplete = () => {
    init(id, {
      onQueueRetried,
      onTrialRecipientLimit
    })
    initCustomFields()
  }

  useEffect(() => {
    if (!loaded) {
      initComplete()
    }
  }, [loaded])

  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [])

  /* modale scelta destinatari */
  const {
    show: showModaleSegmentation,
    error: errorModaleSegmentation,
    severity: severityModaleSegmentation,
    loading: loadingModaleSegmentation,
    groups,
    excludedGroups,
    filters,
    section,
    queued,
    simulationEnabled,
    simulationLoading,
    simulationLoaded,
    simulationResult,
    simulationDiff
  } = useRecoilValue(modalSendingSegmentationState)
  const setModalSegmentationState = useSetRecoilState(
    modalSendingSegmentationState
  )
  const closeModalSegmentation = useResetRecoilState(
    modalSendingSegmentationState
  )
  const handleSimulate = useRecoilCallback(
    sendingSegmentationSimulateCallback,
    []
  )
  /* ********* */

  /* modale salvataggio configurazione mittente */
  const [{ show: showSaveModal }, setSaveConfigModal] = useRecoilState(
    modalSaveDefaultState
  )

  const closeModalSaveConfig = () => {
    setSaveConfigModal(prev => ({
      ...prev,
      show: false
    }))
  }
  const saveConfig = useRecoilCallback(putBeListSenderInfoCallback, [])
  const confirmModalSaveConfig = () => {
    saveConfig()
    setSaveConfigModal(prev => ({
      ...prev,
      show: false
    }))
  }
  /* ********* */

  /** * MODAL DELETE STEP3***/
  const [{ show }, setModalDeleteState] = useRecoilState(modalDeleteState)
  const setButtonsState = useSetRecoilState(manageButtonsState)
  const deleteRecipients = useRecoilCallback(deleteRecipientsCallback, [])
  const onDeleteConfirm = () => {
    deleteRecipients().then(e => {
      if (e) {
        history.push('/messages/email/sendstatus/campaigns')
      }
      setButtonsState(prev => ({
        ...prev,
        retryDisabled: false,
        deleteDisabled: false
      }))
    })
  }
  const onDeleteCancel = () => {
    setButtonsState(prev => ({
      ...prev,
      retryDisabled: false,
      deleteDisabled: false
    }))
    setModalDeleteState(prev => ({
      ...prev,
      show: false
    }))
  }

  return (
    <>
      <Header />
      <ModalConfirmLeave isDirty={isDirty} />
      <Loading open={loading || isFethingModalState} />
      <ModalColumnsManagement />
      <ModalCheckup
        startWhen={loaded}
        messageType={messageType}
        isBee={isBee}
        idMessage={parseInt(id, 0)}
        idCampaign={campaign ? campaign.Id : 0}
      />
      <ModalSendingSegmentation
        show={showModaleSegmentation}
        error={errorModaleSegmentation}
        severity={severityModaleSegmentation}
        loading={loadingModaleSegmentation}
        groups={groups}
        excludedGroups={excludedGroups}
        filters={filters}
        section={section}
        closeModal={closeModalSegmentation}
        setModalState={setModalSegmentationState}
        queued={queued}
        simulationEnabled={simulationEnabled}
        simulationLoading={simulationLoading}
        simulationLoaded={simulationLoaded}
        simulationResult={simulationResult}
        simulationDiff={simulationDiff}
        handleSimulate={handleSimulate}
      />
      <ModalConfirm
        data-mu-test='modal-confirm-save-sender-config'
        show={showSaveModal}
        title={intl.formatMessage({
          id: 'Load.EmailCheckUp.TitleModalDefaulSender'
        })}
        message={intl.formatMessage({
          id: 'Load.EmailCheckUp.TxtModalDefaulSender'
        })}
        onClickCancel={closeModalSaveConfig}
        stringCancel={intl.formatMessage({ id: 'Load.MailupCommon.Cancel' })}
        onClickConfirm={confirmModalSaveConfig}
        stringConfirm={intl.formatMessage({ id: 'Load.MailupCommon.Continue' })}
        showClose
      />
      <ModalDelete
        show={show}
        handleCancel={onDeleteCancel}
        handleConfirm={onDeleteConfirm}
      />
      {/* <ModalSchedule show={showScheduleModal} startDate={startDate} /> */}
      <ToastContainer />
      <Tooltip />

      <PageContainer>
        <ErrorFeedback
          error={error || modalCheckupError}
          severity={severity}
          onClickReload={() => initComplete()}
        />
        {!(error || modalCheckupError) && !loading && !isFethingModalState && (
          <>
            <SegmentationMessageBox />
            <WizardContainer />
          </>
        )}
      </PageContainer>
    </>
  )
}

export default EmailSendingWizard
